import { useCallback, useRef } from "react";

import pluginService from "api/pluginService";
import {
  ECouncilSessionState,
  ECouncilVotingResult,
  ECouncilVotingType,
  ICouncilSession,
} from "dto/Council/ICouncil";
import {
  ICouncilPresence,
  ICouncilVoting,
} from "dto/Council/ICouncilPortal";
import { decodeText } from "./decodeText";
import {
  EObsOutputAction,
  EObsOutputKind,
  EObsOutputState,
  ICouncilStreamState,
  IOsdWriteRequest,
} from "dto/Plugin/councilStream";

// Biuro rady → obsługa transmisji obrad podczas prowadzenia sesji.
//
// To port warstwy transmisyjnej z ModeratorViewModel Magistratu: sterowania OBS
// Studio, napisów OSD (pasek.txt / tytul.txt / mowca.txt) i najazdów kamer PTZ.
// Wykonawcą jest wtyczka desktop na stanowisku realizatora — przeglądarka nie
// dosięgnie ani obs-websocket na pętli zwrotnej, ani kamery po HTTP.
//
// Brzmienie komunikatów siedzi tutaj, a nie we wtyczce: dzięki temu zmiana treści
// paska jest wydaniem front-endu, a nie aktualizacją wtyczki u każdego urzędu.
// Wszystkie teksty są przepisane ze źródła co do znaku.
//
// ODSTĘPSTWA OD MAGISTRATU (wszystkie świadome, każde naprawia realną lukę):
//  * powrót ze sceny głosowania następuje przy ZAKOŃCZENIU i przy ANULOWANIU
//    głosowania. W Magistracie tylko przy osobnym „zamknij głosowanie", a po
//    anulowaniu nie następował nigdy — transmisja zostawała na planszy głosowania;
//  * plakietka mówcy zapisuje się niezależnie od integracji z kamerami (w źródle
//    leżała wewnątrz warunku EnableDahuaIntegration, więc bez kamer nie działała);
//  * przerwa w obradach pisze komunikat na pasku i gasi plakietkę (w źródle
//    zawieszenie nie robiło nic i na pasku zostawał poprzedni komunikat);
//  * zakończenie sesji czyści wszystkie trzy pliki (w źródle tylko pasek, więc
//    po sesji na transmisji zostawał tytuł i nazwisko ostatniego mówcy).

/** Standardowe „zniknij po minucie" — jedyna wartość opóźnienia użyta w Magistracie. */
const BAR_SECONDS = 60;

const majorityLabel = (type: ECouncilVotingType) =>
  type === ECouncilVotingType.Normal ? "zwykła" : "absolutna";

/** „Zakończono głosowanie…" — dokładnie jak w endVotingExecuted. */
const votingResultBar = (voting: ICouncilVoting) => {
  if (voting.Type === ECouncilVotingType.Custom) {
    const distribution = voting.Options.map(
      (o) => `${decodeText(o.Name)}: ${o.Votes}`,
    ).join(", ");

    const chosen = voting.OptionIdResult
      ? voting.Options.find((o) => o.Id === voting.OptionIdResult)?.Name
      : null;

    const result = chosen ? decodeText(chosen) : "głosowanie nierozstrzygnięte";

    return `Zakończono głosowanie, wymagana większość zwykła, rozkład głosów ${distribution}. Wynik głosowania: ${result}`;
  }

  const result =
    voting.Result === ECouncilVotingResult.Positive ? "Przyjęto" : "Odrzucono";

  return (
    `Zakończono głosowanie, wymagana większość ${majorityLabel(voting.Type)}, ` +
    `ZA: ${voting.For}, PRZECIW: ${voting.Against}, WSTRZYMAŁO SIĘ: ${voting.Abstain}. ` +
    `Wynik głosowania: ${result}`
  );
};

/** Druga linia plakietki mówcy — w Magistracie wyłącznie „Radny" / „Radna". */
const speakerFunction = (male: boolean) => (male ? "Radny" : "Radna");

interface Options {
  /** Stan z wtyczki; null, dopóki jej nie ma albo nie odpowiedziała. */
  state: ICouncilStreamState | undefined;
  /** Odświeżenie stanu po akcji, która mogła go zmienić. */
  refresh: () => void;
}

const useCouncilStream = ({ state, refresh }: Options) => {
  // Panel odpytuje wtyczkę cyklicznie, ale akcje prowadzącego lecą asynchronicznie
  // i nie mogą go blokować ani wywracać. Referencja trzyma najświeższy stan bez
  // przebudowywania wszystkich funkcji przy każdym tiku odpytywania.
  const stateRef = useRef<ICouncilStreamState | undefined>(state);
  stateRef.current = state;

  const enabled = !!state?.SupportsStreaming;
  const osdEnabled = enabled && !!state?.OsdPath;
  const camerasEnabled = !!state?.CamerasEnabled;

  /**
   * Transmisja nie może przerwać prowadzenia obrad: gdy OBS nie odpowiada albo
   * zniknie udział sieciowy z napisami, prowadzący ma dalej sterować sesją.
   * Dlatego każda akcja jest „najlepszym wysiłkiem" i tylko loguje niepowodzenie.
   */
  const attempt = useCallback(async (what: string, run: () => Promise<unknown>) => {
    try {
      await run();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Transmisja obrad: ${what} nie powiodło się`, e);
    }
  }, []);

  const osd = useCallback(
    (request: IOsdWriteRequest) => {
      if (!osdEnabled) return;
      void attempt("zapis napisów", () => pluginService.writeOsd(request));
    },
    [attempt, osdEnabled],
  );

  const scene = useCallback(
    (name: string | null | undefined) => {
      if (!enabled || !name || !stateRef.current?.Connected) return;
      void attempt("przełączenie sceny", () =>
        pluginService.setObsScene({ Name: name }),
      );
    },
    [attempt, enabled],
  );

  /** Najazd kamery + przełączenie sceny tej kamery — odpowiednik findCameraAndSetPreset. */
  const gotoPreset = useCallback(
    (cameraId: string | null | undefined, preset: number, changeScene = true) => {
      if (!camerasEnabled) return;
      void attempt("najazd kamery", () =>
        pluginService.gotoCameraPreset({
          CameraId: cameraId ?? null,
          Preset: preset,
          ChangeScene: changeScene,
        }),
      );
    },
    [attempt, camerasEnabled],
  );

  /**
   * „Widok ogólny" to po prostu PIERWSZY preset pierwszej kamery — w Magistracie
   * nie było osobnego pola, decydowała kolejność z ustawień (gotoCameraNormalPreset).
   */
  const gotoNormalPreset = useCallback(
    (changeScene = true) => {
      const camera = stateRef.current?.Cameras?.find((c) => c.Presets.length > 0);
      const preset = camera?.Presets[0];

      if (!camera || !preset) return;

      gotoPreset(camera.Id, preset.Number, changeScene);
    },
    [gotoPreset],
  );

  // ── Zdarzenia sesji ────────────────────────────────────────────────────────

  /**
   * Wejście do panelu obrad: tytuł na transmisji i komunikat oczekiwania.
   *
   * Komunikat oczekiwania wolno wpisać WYŁĄCZNIE przed rozpoczęciem obrad. Panel
   * odmontowuje się przy każdym przejściu na inną zakładkę Biura rady, a wejście
   * z powrotem w trakcie transmisji na żywo kasowałoby pasek z wynikiem głosowania
   * i plakietkę mówiącego właśnie radnego. W Magistracie problem nie istniał, bo
   * ten zapis był w OnLoaded okna moderatora, otwieranego raz na sesję.
   */
  const onPanelOpened = useCallback(
    (session: ICouncilSession | null) => {
      const title = { Text: decodeText(session?.Title) || null };

      if (session && session.State !== ECouncilSessionState.NotStarted) {
        osd({ Title: title });
        return;
      }

      osd({
        Title: title,
        Bar: { Text: "Oczekiwanie na rozpoczęcie sesji", Seconds: null },
        Speaker: { Label: null, Function: null },
      });
    },
    [osd],
  );

  /**
   * Rozpoczęcie obrad uruchamia transmisję: jeśli wtyczka nie jest połączona
   * z OBS, najpierw łączy (StartSession w Magistracie robił dokładnie to przez
   * StartStreamAfterConnect).
   */
  const onSessionStarted = useCallback(async () => {
    // Bez tego na antenie zostałby napis „Oczekiwanie na rozpoczęcie sesji" mimo
    // trwających już obrad. W Magistracie problem nie występował, bo rozpoczęcie
    // sesji samo przechodziło do pierwszego punktu porządku i nadpisywało pasek;
    // tutaj punkt wskazuje prowadzący, więc do tego czasu pasek musi mówić prawdę.
    osd({ Bar: { Text: "Rozpoczęcie obrad", Seconds: BAR_SECONDS } });

    if (!enabled) return;

    await attempt("uruchomienie transmisji", async () => {
      let current = stateRef.current;

      if (!current?.Connected) {
        current = await pluginService.connectCouncilStream();
        stateRef.current = current;
      }

      if (current?.Connected && current.StreamState === EObsOutputState.Stopped) {
        await pluginService.setObsOutput({
          Output: EObsOutputKind.Stream,
          Action: EObsOutputAction.Start,
        });
      }
    });

    refresh();
  }, [attempt, enabled, osd, refresh]);

  const onSessionSuspended = useCallback(() => {
    osd({
      Bar: { Text: "Przerwa w obradach", Seconds: null },
      Speaker: { Label: null, Function: null },
    });

    // Transmisja i nagrywanie celowo lecą dalej — w Magistracie zatrzymanie było
    // w tym miejscu zakomentowane, bo przerwa jest częścią nagrania sesji.
  }, [osd]);

  const onSessionResumed = useCallback(() => {
    osd({ Bar: { Text: "Wznowienie obrad", Seconds: BAR_SECONDS } });
  }, [osd]);

  const onSessionEnded = useCallback(async () => {
    if (enabled && stateRef.current?.Connected) {
      await attempt("zakończenie transmisji", async () => {
        await pluginService.setObsOutput({
          Output: EObsOutputKind.Stream,
          Action: EObsOutputAction.Stop,
        });
        await pluginService.setObsOutput({
          Output: EObsOutputKind.Record,
          Action: EObsOutputAction.Stop,
        });
      });
    }

    // Napisy gasimy PO zatrzymaniu wyjść, żeby ostatnie sekundy transmisji nie
    // poszły z pustą planszą. Magistrat czyścił tylko pasek i zostawiał na antenie
    // tytuł sesji oraz nazwisko ostatniego mówcy.
    osd({ ClearAll: true });

    refresh();
  }, [attempt, enabled, osd, refresh]);

  /** Przejście do punktu porządku obrad — jedyne zdarzenie ruszające wszystkie trzy kanały. */
  const onAgendaItem = useCallback(
    (order: string | null, title: string | null) => {
      osd({
        Bar: {
          Text: `Przejście do punktu ${order}. ${decodeText(title)}`,
          Seconds: BAR_SECONDS,
        },
        Speaker: { Label: null, Function: null },
      });

      gotoNormalPreset();
    },
    [gotoNormalPreset, osd],
  );

  /** Udzielenie głosu radnemu: kamera na jego miejsce i plakietka na transmisji. */
  const onLetSpeak = useCallback(
    (councilor: ICouncilPresence | undefined) => {
      if (!councilor) return;

      osd({
        Speaker: {
          Label: decodeText(councilor.FullName) || null,
          Function: speakerFunction(councilor.Male),
        },
      });

      if (councilor.CameraPreset != null) {
        gotoPreset(councilor.CameraId, councilor.CameraPreset);
      } else {
        // Radny bez własnego presetu — kamera zostaje na widoku ogólnym, tak jak
        // w LetSpeakExecuted, gdzie brak presetu cofał kamerę do pierwszego widoku.
        gotoNormalPreset();
      }
    },
    [gotoNormalPreset, gotoPreset, osd],
  );

  /**
   * Ręczne „ustaw kamerę na radnego". W odróżnieniu od udzielenia głosu NIE zmienia
   * sceny OBS — tak samo jak SetCameraOnCounciliorExecuted w Magistracie.
   */
  const onPointCameraAt = useCallback(
    (councilor: ICouncilPresence) => {
      osd({
        Speaker: {
          Label: decodeText(councilor.FullName) || null,
          Function: speakerFunction(councilor.Male),
        },
      });

      if (councilor.CameraPreset != null)
        gotoPreset(councilor.CameraId, councilor.CameraPreset, false);
    },
    [gotoPreset, osd],
  );

  // ── Zdarzenia głosowania ───────────────────────────────────────────────────

  const onVotingStarted = useCallback(
    async (description: string | null) => {
      osd({
        Bar: { Text: `Trwa głosowanie: ${decodeText(description)}`, Seconds: null },
      });

      if (!enabled || !stateRef.current?.Connected) return;

      await attempt("wejście na scenę głosowania", () =>
        pluginService.setObsVotingScene({ Active: true }),
      );
    },
    [attempt, enabled, osd],
  );

  const leaveVotingScene = useCallback(async () => {
    if (!enabled || !stateRef.current?.Connected) return;

    await attempt("powrót ze sceny głosowania", () =>
      pluginService.setObsVotingScene({ Active: false }),
    );
  }, [attempt, enabled]);

  const onVotingEnded = useCallback(
    async (voting: ICouncilVoting) => {
      osd({ Bar: { Text: votingResultBar(voting), Seconds: BAR_SECONDS } });

      await leaveVotingScene();

      // Kamera wraca na widok ogólny bez ruszania sceny — scenę przywrócił już
      // powrót ze sceny głosowania (w Magistracie oba kanały biły się o scenę).
      gotoNormalPreset(false);
    },
    [gotoNormalPreset, leaveVotingScene, osd],
  );

  const onVotingCanceled = useCallback(async () => {
    osd({ Bar: { Text: "Anulowano głosowanie", Seconds: BAR_SECONDS } });

    await leaveVotingScene();

    gotoNormalPreset(false);
  }, [gotoNormalPreset, leaveVotingScene, osd]);

  // ── Sterowanie ręczne z panelu ─────────────────────────────────────────────

  const connect = useCallback(async () => {
    await attempt("połączenie z OBS Studio", () =>
      pluginService.connectCouncilStream(),
    );
    refresh();
  }, [attempt, refresh]);

  const disconnect = useCallback(async () => {
    await attempt("rozłączenie z OBS Studio", () =>
      pluginService.disconnectCouncilStream(),
    );
    refresh();
  }, [attempt, refresh]);

  const runObs = useCallback(async () => {
    await attempt("uruchomienie OBS Studio", () => pluginService.runObs());
    refresh();
  }, [attempt, refresh]);

  /**
   * Jawne Start/Stop, a nie Toggle: panel pokazuje stan sprzed najwyżej trzech sekund,
   * więc przełącznik potrafiłby zgasić transmisję uruchomioną chwilę wcześniej z OBS.
   * Przy jawnym poleceniu najgorszym skutkiem nieaktualnego stanu jest polecenie bez
   * efektu — wtyczka pomija odpowiedzi „wyjście już w tym stanie".
   */
  const setOutput = useCallback(
    async (kind: EObsOutputKind, action: EObsOutputAction) => {
      await attempt("przełączenie wyjścia OBS", () =>
        pluginService.setObsOutput({ Output: kind, Action: action }),
      );
      refresh();
    },
    [attempt, refresh],
  );

  const selectScene = useCallback(
    async (name: string) => {
      await attempt("przełączenie sceny", () =>
        pluginService.setObsScene({ Name: name }),
      );
      refresh();
    },
    [attempt, refresh],
  );

  return {
    enabled,
    camerasEnabled,
    osdEnabled,
    // zdarzenia sesji
    onPanelOpened,
    onSessionStarted,
    onSessionSuspended,
    onSessionResumed,
    onSessionEnded,
    onAgendaItem,
    onLetSpeak,
    onPointCameraAt,
    // zdarzenia głosowania
    onVotingStarted,
    onVotingEnded,
    onVotingCanceled,
    // sterowanie ręczne
    connect,
    disconnect,
    runObs,
    setOutput,
    selectScene,
    gotoPreset,
    scene,
  };
};

export default useCouncilStream;
